import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { validateToken } from '../../utils/tokenValidator';
import API_CONFIG from '../../config/apiConfig';

/**
 * TokenExpirationMonitor - Global component that monitors JWT token expiration
 * and automatically redirects to login when the token expires.
 * 
 * Features:
 * - Checks token expiration every 30 seconds
 * - Proactively refreshes token 5 minutes before expiration
 * - Redirects to login if token is expired
 * - Cleans up auth state on expiration
 */
export const TokenExpirationMonitor: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const checkTokenExpiration = () => {
      const currentToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!currentToken) {
        // No token, user is not authenticated
        return;
      }

      const validation = validateToken(currentToken);
      
      if (!validation.valid) {
        // Token is invalid or expired
        if (validation.expired) {
          console.log('[TokenExpirationMonitor] Token expired, logging out');
          handleTokenExpired();
        }
        return;
      }

      // Check if we should refresh the token
      if (validation.shouldRefresh) {
        console.log('[TokenExpirationMonitor] Token expiring soon, attempting refresh');
        attemptTokenRefresh();
      }

      // Schedule a refresh 5 minutes before expiration
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      
      // Schedule refresh based on time remaining
      if (validation.timeRemaining > 0 && validation.timeRemaining !== Infinity) {
        const refreshIn = Math.max(0, (validation.timeRemaining - 300) * 1000);
        if (refreshIn > 0) {
          refreshTimeoutRef.current = setTimeout(() => {
            console.log('[TokenExpirationMonitor] Scheduled token refresh');
            attemptTokenRefresh();
          }, refreshIn);
        }
      }
    };

    const attemptTokenRefresh = async () => {
      const storedRefreshToken = localStorage.getItem('refreshToken');
      
      if (!storedRefreshToken) {
        console.log('[TokenExpirationMonitor] No refresh token available');
        // If no refresh token, we can't refresh - but don't immediately expire
        // The token might still be valid for a while
        return;
      }

      try {
        // Use auth service URL for refresh endpoint
        const authServiceUrl = API_CONFIG.AUTH_SERVICE_URL || API_CONFIG.BASE_URL;
        const response = await fetch(
          `${authServiceUrl}/api/v1/auth/refresh`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              refresh_token: storedRefreshToken, // Match the expected field name
            }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          
          if (data.success && data.data?.access_token) {
            // Update tokens in localStorage
            localStorage.setItem('authToken', data.data.access_token);
            localStorage.setItem('token', data.data.access_token);
            
            if (data.data.refresh_token) {
              localStorage.setItem('refreshToken', data.data.refresh_token);
            }

            // Update auth store
            const authStore = useAuthStore.getState();
            authStore.setToken(data.data.access_token);

            console.log('[TokenExpirationMonitor] Token refreshed successfully');
            
            // Schedule next check based on new token expiration
            checkTokenExpiration();
          } else {
            console.error('[TokenExpirationMonitor] Invalid refresh response');
            handleTokenExpired();
          }
        } else {
          console.error('[TokenExpirationMonitor] Refresh failed:', response.status);
          // Only handle as expired if it's a 401 (unauthorized)
          // Other errors might be temporary network issues
          if (response.status === 401) {
            handleTokenExpired();
          }
        }
      } catch (error) {
        console.error('[TokenExpirationMonitor] Error refreshing token:', error);
        // Don't immediately expire on network errors
        // The user might still have a valid token
      }
    };

    const handleTokenExpired = () => {
      // Clear all auth data
      localStorage.removeItem('authToken');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');

      // Update auth store
      logout();

      // Dispatch auth error event
      const event = new CustomEvent('auth-error', {
        detail: { message: 'Your session has expired. Please log in again.' }
      });
      window.dispatchEvent(event);

      // Navigate to login
      navigate('/login');
    };

    // Initial check
    checkTokenExpiration();

    // Set up periodic checking every 30 seconds
    checkIntervalRef.current = setInterval(checkTokenExpiration, 30000);

    // Cleanup
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [navigate, logout]);

  // This component doesn't render anything
  return null;
};

export default TokenExpirationMonitor;